//@flow
import {
    transporter as transporterApi,
    bulkTransporterFetch as bulkTransporterFetchApi,
    disableTransporter as disableTransporterApi,
    bulkDisableTransporter as bulkDisableTransporterApi,
    validateEntityIds as validateEntityIdsApi
} from 'common/middleware/api';
import { showToast } from 'common/redux/actions/toast';
import { ToastTypes } from 'common/components/core/toast/Toast';
import PaginatedEntityActions from 'common/redux/actions/PaginatedEntityActions';
import I18nHelper from 'common/helpers/I18nHelper';

import type { MasterTransporter } from 'common/models/internal/MasterTransporter';
import type { Dispatch } from 'redux';
import { ENTITY_NAME_ID } from 'common/constants/entity/transporterMaster';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

type Params = {
    clientId: string,
    transporterId?: string
};

class TransporterActions extends PaginatedEntityActions<
    MasterTransporter,
    Params
> {
    entityNameId = ENTITY_NAME_ID;
    injectEntities(
        params: Params,
        entities: Array<Object>,
        body: Object,
        options: Object = {}
    ) {
        return {
            type: this.types.FETCH_ENTITIES_SUCCESS,
            params,
            entities,
            body,
            options
        };
    }
    superAddEntity = this.addEntity;

    addEntity = (
        params: { clientId: string, transporterId: string },
        entity: MasterTransporter,
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (transporter: MasterTransporter) => {
                        const toast = {
                            message: nsTranslate('addEntitySuccessMessage', {
                                entityNameId: ENTITY_NAME_ID
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(transporter);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError: true
                };
                dispatch(this.superAddEntity(params, entity, options));
            });
        };
    };

    superPatchEntity = this.patchEntity;

    patchEntity = (
        params: { clientId: string, transporterId: string },
        oldEntity: MasterTransporter,
        newEntity: MasterTransporter,
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const [oldEntitySanitized, newEntitySanitized] = [
                    oldEntity,
                    newEntity
                ].map(entity => entity.sanitize({ contact: true }));
                const options = {
                    onSuccess: (transporter: MasterTransporter) => {
                        const toast = {
                            message: nsTranslate('savedChangesMessage'),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(transporter);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError: true
                };
                dispatch(
                    this.superPatchEntity(
                        params,
                        oldEntitySanitized,
                        newEntitySanitized,
                        options
                    )
                );
            });
        };
    };

    superDisableEntity = this.disableEntity;

    disableEntity = (
        params: { clientId: string, transporterId: string },
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (transporter: MasterTransporter) => {
                        const toast = {
                            message: nsTranslate('deleteEntitySuccessMessage', {
                                entityNameId: ENTITY_NAME_ID
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(transporter);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError: true
                };
                dispatch(this.superDisableEntity(params, options));
            });
        };
    };
}

export default new TransporterActions(
    'TRANSPORTER',
    transporterApi,
    bulkTransporterFetchApi,
    disableTransporterApi,
    bulkDisableTransporterApi,
    validateEntityIdsApi
);
